// @theme component
import React, { FC, useEffect, useState, useCallback } from 'react';
import { componentService } from '@core/js/services/component.service';
import { useDTAG } from '@theme/js/hooks/useDTAG';
import { Jackpot } from '@core/js/components/common/Jackpot/Jackpot';

type CustomJackpotProps = {
    dtag: string,
}

type Jackpot = {
    id?: string,
    description?: string,
    currencyCode?: string,
    interval?: number,
    step?: number,
    amount?: number,
    originAmount?: number
}

export const CustomJackpot: FC<CustomJackpotProps> = ({ dtag = '' }) => {
    const [jackpots, setJackpots] = useState<Jackpot[]>();

    const jackpotUrl = useDTAG(dtag);

    const parseJackpot = useCallback((json: any) => {
        const data = json instanceof Array ? json[0] : json;

        let jackpotsData = {};

        if (data) {
            const amount = Number(data.JackpotAmount) || 0;

            jackpotsData = {
                currencyCode: data.CurrencySymbol,
                interval: Number(data.Interval) || 100,
                step: Number(data.Increment) || 0.01,
                amount: amount * (Number(data.Offset) || 0.99),
                originAmount: amount
            };
        }

        setJackpots([jackpotsData]);
    }, []);

    const parsePlayngo = useCallback((json: any) => {
        const data = json instanceof Array ? json.slice(0, 2) : json;

        let jackpotsData = [{}];

        if (data) {
            jackpotsData = data.map((jackpot: any) => ({
                id: jackpot.JackpotId,
                description: jackpot.Description || '',
                currencyCode: window.Playtech.API?.formatter.getCurrencySymbol(jackpot.Currency),
                interval: Number(jackpot.Interval) || 100,
                step: Number(jackpot.Increment) || 0.01,
                amount: (Number(jackpot.BaseAmount) || 0) * (Number(jackpot.Offset) || 0.99),
                originAmount: Number(jackpot.BaseAmount) || 0
            }));
        }

        setJackpots(jackpotsData);
    }, []);

    const fetchJackpots = useCallback(async () => {
        try {
            const response = await fetch(jackpotUrl as string);
            const json = await response.json();

            switch (dtag) {
                case 'JACKPOT_TICKER':
                    parseJackpot(json);
                    break;
                case 'PLAYNGO_TICKER':
                    parsePlayngo(json);
                    break;
                default:
                    break;
            }
        } catch (e) {
            console.log(`Coulnd't fetch jackpot, ${e}`);
        }
    }, [jackpotUrl, dtag, parseJackpot, parsePlayngo]);

    useEffect(() => {
        fetchJackpots();
    }, [jackpotUrl, fetchJackpots]);

    if (!jackpots?.length) {
        return null;
    }

    return (jackpots.length === 1
        ? <Jackpot {...jackpots[0]} className="custom-jackpot-ticker" />
        : jackpots.map(jackpot => (
            <div className="playngo-ticker-item" key={jackpot?.id}>
                <div className="playngo-ticker-description">{jackpot?.description}</div>
                <div className="playngo-ticker-value" id={`playngo-ticker-${jackpot?.id}`}>
                    <Jackpot {...jackpot} className="custom-jackpot-ticker" />
                </div>
            </div>
        ))) as JSX.Element;
};

componentService.registerWebContentComponents({ CustomJackpot });
